import * as React from 'react';
import styles from './OnlineModal.module.scss';
import WebCamIcon from 'components/assets/svg/exam/webcam_icon.svg';
import { ExtendedSelect, UserVideo } from 'components';
import cn from 'classnames';
import { Button } from 'react-bootstrap';
import TestVideoPoster from 'components/assets/gif/test_camera.gif';
import TestVideoErrorPoster from 'components/assets/gif/not_allowed_camera.gif';

const VideoTest: React.FC<IProps> = (props) => {
    const steps: IStep[] = [
        {
            index: 1,
            text: '1. Нажмите на кнопку «Начать проверку веб-камеры»',
            buttonText: 'Начать проверку веб-камеры',
        },
        {
            index: 2,
            text: '2. Разрешите сайту использовать вашу камеру',
            buttonText: 'Начать проверку веб-камеры',
            disableButton: true,
            video: TestVideoPoster,
        },
        {
            index: 3,
            text: '3. Улыбнитесь!',
            buttonText: 'Начать проверку веб-камеры',
            disableButton: true,
            emoji: '😁',
        },
        {
            index: 4,
            text: '4. Если вы видите себя - значит все в порядке',
            buttonText: 'Вижу себя хорошо',
            emoji: '👍 🎉',
        },
    ];

    const errorText = [
        'Мы заметили, что вы отклонили запрос на предоставление доступа к веб-камере.',
        'В соответствии с Правилами проведения экзамена, вы должны предоставить доступ к вашей веб-камере.',
        'Для исправления ситуации, пожалуйста, следуйте видео-инструкции слева или нажмите на кнопку «Начать проверку заново».',
    ];

    const [currStep, setStep] = React.useState<IStep | null>(steps[0]);
    const [isError, setError] = React.useState(false);
    const [stream, setStream] = React.useState<MediaStream | null>(null);

    const [videoDevices, setVideoDevices] = React.useState<MediaDeviceInfo[]>([]);
    const [currentVideoDevice, setCurrentVideoDevice] = React.useState<string | null>(null);

    const getVideoDevices = React.useCallback(async () => {
        try {
            const devices = await navigator.mediaDevices.enumerateDevices();
            return devices.filter(d => d.kind === 'videoinput')
            .map(d => ({ ...d.toJSON(), label: d.label.split('(')[0] })) as MediaDeviceInfo[];
        } catch (e) {
            console.error('Ошибка при получении списка устройств:', e);
            return [] as MediaDeviceInfo[];
        }
    }, []);


    React.useEffect(() => {
        getVideoDevices().then(setVideoDevices);
    }, []);

    const handleStart = React.useCallback(async () => {
        if (navigator.mediaDevices) {
            try {
                setStep(steps[1]);
                const mediaStream = await navigator.mediaDevices.getUserMedia({
                    video: true,
                });

                getVideoDevices().then(setVideoDevices);

                setStep(steps[3]);
                setStream(mediaStream);
                setCurrentVideoDevice(mediaStream.getVideoTracks()[0].getSettings().deviceId || null);
            } catch (e) {
                setError(true);
            }
        }
    }, []);

    const handleChangeVideoDevice = async (deviceId: string) => {
        setCurrentVideoDevice(deviceId);
        if (navigator.mediaDevices) {
            try {
                const mediaStream = await navigator.mediaDevices.getUserMedia({
                    video: {
                        deviceId,
                    },
                });

                setStream(mediaStream);
            } catch (e) {
                setError(true);
            }
        }
    };

    const handler = React.useMemo(() => {
        if (isError) {
            return () => {
                setStep(steps[0]);
                setError(false);
            };
        }

        if (!currStep) {
            return props.onNextTest;
        }

        if (currStep.index === 1) {
            return handleStart;
        }

        if (currStep.index === 4) {
            return () => setStep(null);
        }

        return () => {
        };
    }, [currStep, isError]);

    const buttonText = React.useMemo(() => {
        if (isError) {
            return 'Начать проверку заново';
        }

        if (currStep) {
            return currStep.buttonText;
        }

        return 'Перейти к следующему шагу';
    }, [currStep, isError]);

    React.useEffect(() => () => {
        if (stream) {
            stream.getTracks().forEach(track => track.stop());
        }
    }, [stream]);

    const videoPoster = isError ? TestVideoErrorPoster : currStep ? currStep.video : undefined;

    return (
      <div className={styles.testContainer}>
          <div>
              {(!currStep || currStep.index > 1) && (
                <UserVideo
                  muted
                  containerClassName={styles.videoContainer}
                  className={cn(styles.video, { [styles.autoHeight]: !!videoPoster })}
                  videoStream={stream || undefined}
                  poster={videoPoster}
                  withoutFullScreen
                />
              )}
              {videoDevices.length > 1 && (!currStep || currStep.index > 1) && (
                <ExtendedSelect
                  className="mt-3 mr-3"
                  smSize
                  name="videoDevicesSelect"
                  options={videoDevices.map(d => ({ label: d.label, value: d.deviceId }))}
                  onChange={e => handleChangeVideoDevice(e.value)}
                  value={currentVideoDevice}
                />
              )}
          </div>
          <div className={styles.testStepper}>
              {currStep ? (
                isError ? (
                  <>
                      <h4>Нет доступа к камере ️<span aria-label="who knows emoji" role="img">🤷🏻‍♂</span>️</h4>
                      {errorText.map((t, i) => (
                        <div key={i} className={styles.errorText}>
                            {t}
                        </div>
                      ))}
                  </>
                ) : (
                  <>
                      <h4>Как пройти тест веб-камеры?</h4>
                      <ul>
                          {steps.map((step, i) => (
                            <li key={step.index} className={cn({ [styles.active]: i + 1 === currStep.index })}>
                                {step.text} {step.emoji ? <span>{step.emoji}</span> : null}
                            </li>
                          ))}
                      </ul>
                  </>
                )
              ) : (
                <div className={styles.end}>
                    <img src={WebCamIcon} alt="WebCamIcon" />
                    Веб-камера в порядке!
                </div>
              )}
              <Button
                disabled={Boolean(currStep && currStep.disableButton && !isError)}
                onClick={handler}
              >
                  {buttonText}
              </Button>
          </div>
      </div>
    );
};

interface IProps {
    onNextTest: () => void;
}

interface IStep {
    index: number;
    text: string;
    buttonText: string;
    disableButton?: boolean;
    video?: string;
    emoji?: string;
}

export default VideoTest;
